import { DeviceType, FIRMWARE_HARDWARE, FirmwareKind, FirmwareType, firmwareKindsForDevice } from '@ixicare/resources';
import { t } from '~i18n';

// Gateways take two firmwares, so the device type alone does not tell them apart.
const isGatewayFirmware = (type: FirmwareType) =>
  FIRMWARE_HARDWARE[type]?.deviceTypes.includes(DeviceType.GATEWAY) ?? false;

export const firmwareKindLabel = (kind: FirmwareKind): string => t(`firmware.kind.${kind}`);

// Only needed where the device type takes both kinds, i.e. the ixi Pro.
const kindSuffix = (type: FirmwareType): string | undefined => {
  const hardware = FIRMWARE_HARDWARE[type];
  return hardware && hardware.deviceTypes.some((deviceType) => firmwareKindsForDevice(deviceType).length > 1)
    ? firmwareKindLabel(hardware.kind)
    : undefined;
};

// "ixi Lite, Button" for device firmware, "ixi Pro · Firmware" when the device takes both kinds,
// "Gateway BLE" for gateways, the raw type otherwise.
export const firmwareDevicesLabel = (type: FirmwareType, chip?: string): string => {
  const hardware = FIRMWARE_HARDWARE[type];
  if (!hardware) return type;
  if (isGatewayFirmware(type)) return hardware.chip;
  const devices = hardware.deviceTypes.map((deviceType) => t(`device.types.${deviceType.toLowerCase()}`)).join(', ');
  return [chip ? `${devices} (${chip})` : devices, kindSuffix(type)].filter(Boolean).join(' · ');
};

// "B7 · hw 1.1.0 · MINEW_B7_BLE": what TechOps recognises the firmware by.
export const firmwareHardwareLabel = (type: FirmwareType): string => {
  const hardware = FIRMWARE_HARDWARE[type];
  const chip = isGatewayFirmware(type) ? undefined : hardware?.chip;
  return [chip, hardware?.hwVersion && `hw ${hardware.hwVersion}`, type].filter(Boolean).join(' · ');
};

// "ixi Lite, Button (B7)", "ixi Pro (S9) · Firmware".
export const firmwareTypeOptionLabel = (type: FirmwareType): string =>
  firmwareDevicesLabel(type, FIRMWARE_HARDWARE[type]?.chip);

// "tequinity/2.2.7" -> "2.2.7", so the reported version compares to an uploaded one.
export const plainVersion = (version?: string) => version?.match(/\d+(\.\d+)+/)?.[0];
